import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { ROOMTYPES, STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createRoom,
  getRoomByID,
  RoomImageUpload,
  updateRoom,
} from "./services/room.services";
import { clearRoomDetails } from "./roomSlice";
import FormTextArea from "../../components/formFields/FormTextArea";
import { getFile } from "../../_helper/helper";
import { getAllActiveFacility } from "../facility/services/facility.service";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import FormCheckbox from "../../components/formFields/FormCheckbox";

const CreateRoom: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const RoomID = location.state?.RoomID;
  const RoomDetail = useAppSelector((state) => state.room.RoomDetail);
  const formLoading = useAppSelector((state) => state.room.formLoading);
  const FacilityList = useAppSelector((state) => state.facility.FacilityList);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [RoomDetail]);

  useEffect(() => {
    // Fetch active facilities on component mount
    dispatch(getAllActiveFacility());

    if (RoomID) {
      dispatch(getRoomByID(RoomID));
    }

    return () => {
      dispatch(clearRoomDetails());
    };
  }, [RoomID, dispatch]);

  const handleSubmit = async (values: any) => {
    const formData = {
      RoomTitle: values.RoomTitle ?? "",
      RoomCategory: values.RoomCategory ?? "",
      Description: values.Description ?? "",
      Rate: values.Rate ? parseFloat(values.Rate) : 0,
      SqFoot: values.SqFoot ?? "",
      BedSize: values.BedSize ?? "",
      Adults: values.Adults ?? "",
      RoomViewType: values.RoomViewType ?? "",
      FacilityID: values.FacilityID ? values.FacilityID?.join(",") : "",
      SequenceNo: values.SequenceNo ? parseInt(values.SequenceNo) : 0,
      IsActive: values.IsActive === 1,
      IsFeatured: values.IsFeatured,
    };

    const ImageData = new FormData();
    const RoomImage =
      values?.Image && typeof values?.Image !== "string"
        ? getFile(values?.Image)
        : "";

    try {
      if (RoomID) {
        const editPayload = {
          ...formData,
          ID: RoomID,
        };

        const response = await dispatch(updateRoom(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Image && typeof values?.Image !== "string") {
            ImageData.append("Image", RoomImage);
            ImageData.append("ID", RoomID);
            const imageResponse = await dispatch(RoomImageUpload(ImageData));
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/room");
            } else {
              navigate("/room");
            }
          } else {
            navigate("/room");
          }
        }
      } else {
        const response = await dispatch(createRoom(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", RoomImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(RoomImageUpload(ImageData));
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/room");
            } else {
              navigate("/room");
            }
          } else {
            navigate("/room");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // Prepare facility options for dropdown
  const facilityOptions =
    FacilityList?.map((facility: any) => ({
      value: facility.ID,
      label: facility.Title,
    })) || [];

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={RoomDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {RoomID ? "Update Room" : "Add Room"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="RoomTitle"
                      label="Room Title"
                      placeholder="Room Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="RoomCategory"
                      label="Room Category"
                      placeholder="Room Category"
                      validator={requiredValidator}
                      component={FormSelectionField}
                      astrike={true}
                      options={ROOMTYPES}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Rate"
                      label="Rate"
                      placeholder="Rate"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      type="number"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Description"
                      label="Description"
                      placeholder="Description"
                      component={FormTextArea}
                      rows={2}
                      // style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="SqFoot"
                      label="Square Footage"
                      placeholder="e.g., 360 sq.ft"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="BedSize"
                      label="Bed Size"
                      placeholder="e.g., Queen Size"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Adults"
                      label="Adults Capacity"
                      placeholder="e.g., 2"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="RoomViewType"
                      label="Room View Type"
                      placeholder="e.g., Garden View"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="FacilityID"
                      label="Facility"
                      placeholder="Select Facility"
                      component={FormMultiSelectionFiled}
                      validator={requiredValidator}
                      astrike={true}
                      options={facilityOptions}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="SequenceNo"
                      label="Sequence Number"
                      placeholder="Sequence Number"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      type="number"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      justifyContent="start"
                      name={`Image`}
                      label="Room Image"
                      type="file"
                      accept="image/*"
                      alternateName="Room Image"
                      component={FormImageUpload}
                      validator={requiredArrayValidator}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => ({
                        value: status?.value,
                        label: status?.label,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      width: "100%",
                      margin: "18px 0px 0px",
                      padding: "0px 10px 8px",
                      fontWeight: 700,
                      background: "whitesmoke",
                      borderRadius: 5,
                      height: 48,
                    }}
                  >
                    <Field
                      name="IsFeatured"
                      label="Featured Room"
                      placeholder="Featured Room"
                      component={FormCheckbox}
                      labelClassName="fw-600"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      marginTop: 35,
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={RoomID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || formLoading}
                        loading={formLoading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/room")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateRoom;
